import { DeleteOutline } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions, DialogTitle, Tooltip
} from "@mui/material";

import {
  pickNotDeprecated,
  useCan,
  useDelete,
  useMutationMode,
  useResource,
  useTranslate,
  useWarnAboutChange
} from "@refinedev/core";
import { DeleteButtonProps } from "@refinedev/mui";
import React from "react";

export function isValidKey(
  key: string | number | symbol,
  object: object
): key is keyof typeof object {
  return key in object;
}
type ExtendedDeleteButtonProps = DeleteButtonProps & {
  dataUpdate: (e:any)=> void
};

export const RoomMembersDelBtn: React.FC<ExtendedDeleteButtonProps> = ({
  resource: resourceNameFromProps,
  recordItemId,
  onSuccess,
  mutationMode: mutationModeProp,
  children,
  successNotification,
  errorNotification,
  hideText = false,
  accessControl,
  meta,
  metaData,
  dataProviderName,
  confirmTitle,
  confirmOkText,
  confirmCancelText,
  svgIconProps,
  invalidates,
  dataUpdate,
  ...rest
}) => {
  const accessControlEnabled = accessControl?.enabled ?? true;
  const hideIfUnauthorized = accessControl?.hideIfUnauthorized ?? false;
  const translate = useTranslate();
  const { id, resource } = useResource(
    resourceNameFromProps
  );
  const { mutationMode: mutationModeContext } = useMutationMode();

  const mutationMode = mutationModeProp ?? mutationModeContext;

  const { mutate, isLoading, variables } = useDelete();

  const { data } = useCan({
    resource: resource?.name,
    action: "delete",
    params: { id: recordItemId ?? id, resource },
    queryOptions: {
      enabled: accessControlEnabled,
    },
  });
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleCloseOnConfirm = () => {
    if ((recordItemId ?? id) && resource?.name) {
      setWarnWhen(false);
      dataUpdate(true);
      setOpen(false);
      mutate(
        {
          id: recordItemId ?? id ?? "",
          resource: 'room-member',
          mutationMode,
          successNotification,
          errorNotification,
          meta: pickNotDeprecated(meta, metaData),
          metaData: pickNotDeprecated(meta, metaData),
          dataProviderName,
          invalidates,
        },
        {
          onSuccess: (value) => {
            onSuccess && onSuccess(value);
          },
        }
      );
    }
  };

  const { sx, ...restProps } = rest;

  const { setWarnWhen } = useWarnAboutChange();

  if (accessControlEnabled && hideIfUnauthorized && !data?.can) {
    return null;
  }

  return (
    <div>
      <Tooltip title='Delete'>
        <LoadingButton
          color="error"
          onClick={handleClickOpen}
          disabled={data?.can === false}
          loading={(recordItemId ?? id) === variables?.id && isLoading}
          startIcon={!hideText && <DeleteOutline {...svgIconProps} />}
          sx={{ minWidth: 0, ...sx }}
          loadingPosition={hideText ? "center" : "start"}
          {...restProps}
        >
          {hideText ? (
            <DeleteOutline fontSize="small" {...svgIconProps} />
          ) : (
            children ?? translate("buttons.delete", "Delete")
          )}
        </LoadingButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {confirmTitle ?? translate("buttons.confirm", "Are you sure?")}
        </DialogTitle>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button onClick={handleClose}>
            {confirmCancelText ?? translate("buttons.cancel", "Cancel")}
          </Button>
          <Button color="error" onClick={handleCloseOnConfirm} autoFocus>
            {confirmOkText ?? translate("buttons.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
